// Resolução PURA e determinística da ORDEM de episódios de uma série e do
// "próximo episódio" pro auto-advance do player.
//
// ── Bug de classe que isto conserta (reportado ao vivo pelo Fernando) ──
// O auto-advance de série "pulava pra uma temporada aleatória (normalmente a
// última)" em vez de ir pro próximo episódio na ordem. A causa NÃO era sort por
// string (o player já ordenava numérico) e sim a DERIVAÇÃO da temporada:
//
//   O player derivava a temporada de cada episódio de `ep.season` (campo
//   por-episódio do Xtream) com fallback pra chave do mapa. `ep.season` é
//   NOTORIAMENTE sujo em provider Xtream (constante, 0, ou o total de
//   temporadas). Quando ele vem, por ex., = última temporada em TODOS os
//   episódios, o player colapsa a série inteira numa única "temporada", e o
//   "próximo" (índice seguinte no flat) cai num episódio de outra temporada.
//   Pior: a FICHA (SeriesDetailsScreen) derivava a temporada da CHAVE do mapa
//   `episodes` — então o botão "Próximo TxEy" mostrava um episódio e o
//   auto-advance tocava OUTRO. Fonte de verdade divergente = classe do bug.
//
// ── Invariante ──
// No `series_info` do Xtream, `seasons` (mapa `episodes`) é agrupado POR
// TEMPORADA: a CHAVE do mapa É o agrupamento autoritativo da temporada. Este
// módulo trata a chave como autoritativa e só cai em `ep.season` quando a chave
// não dá um número de temporada útil. É um RESOLVEDOR único, usado pela ficha,
// pelo player (auto-advance + drawer) e pelo snapshot de progresso.
//
// ⚠️ Resolvedor único NÃO é fonte única (corrigido em 27/09/2026 — a frase
// anterior dizia "Uma ÚNICA fonte de verdade ... elas nunca mais divergem"):
//   - a FICHA alimenta este módulo com o payload do SSE (`useSeriesInfoStream`) e
//     renderiza a grade de episódios pelo array CRU da temporada
//     (`seasons[activeSeason]`), sem o dedup por id daqui;
//   - o PLAYER alimenta com o endpoint síncrono (`api.getSeriesInfo`) e anda na
//     lista DEDUPADA (`flat[idx + 1]`).
// Payloads diferentes podem dar listas diferentes, e o dedup por id faz a ficha
// mostrar episódios que o auto-advance não visita. Enquanto as duas telas não
// consumirem o MESMO payload pela MESMA lista, "ficha e player concordam" não é
// garantido por este arquivo.
//
// Tolerante ao schema (#497): aceita `seasons`/arrays/episódios null/undefined
// e ids/campos ausentes sem lançar.

/** Shape mínimo de episódio que o resolvedor precisa. Genérico carrega o resto
 *  (title/thumb/info) pra quem consome, sem acoplar o resolver a display. */
export interface EpisodeLike {
  id: string
  episode_num?: string
  season?: number
}

/** Episódio achatado + ordenado. `episode` é a referência crua (para o chamador
 *  formatar título/thumb). `seasonNumber` é NaN quando a temporada não é
 *  numérica (ex "Especiais") — esses ordenam SEMPRE por último. */
export interface FlatEpisode<T extends EpisodeLike = EpisodeLike> {
  id: string
  seasonNumber: number
  episodeNumber: number
  /** Chave CRUA da temporada no mapa `seasons` (preserva "Especiais", "0" etc.).
   *  A ficha compara isto com `activeSeason`/`seasonKeys` (chaves cruas). */
  seasonKey: string
  /** Posição na 1ª passada — tiebreak determinístico e estável. */
  order: number
  episode: T
}

export type SeasonsMap<T extends EpisodeLike> =
  | Record<string, ReadonlyArray<T> | null | undefined>
  | null
  | undefined

/** NaN (temporada não-numérica) vira +Infinity pra cair pro fim da ordenação. */
function seasonSortValue(n: number): number {
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
}

/** Número de temporada AUTORITATIVO: a chave do mapa manda; `ep.season` é só
 *  fallback quando a chave não é um número de temporada positivo. */
function seasonNumberFor(seasonKey: string, ep: EpisodeLike): number {
  const fromKey = Number(seasonKey)
  if (Number.isFinite(fromKey) && fromKey > 0) return fromKey
  if (typeof ep.season === 'number' && Number.isFinite(ep.season) && ep.season > 0) {
    return ep.season
  }
  // Chave "0" (especiais no padrão TMDB) → 0. Chave não-numérica → NaN (último).
  return Number.isFinite(fromKey) ? fromKey : Number.NaN
}

/** Ordena chaves de temporada: numéricas ↑, depois não-numéricas (alfabético). */
function compareSeasonKeys(a: string, b: string): number {
  const na = Number(a)
  const nb = Number(b)
  const fa = Number.isFinite(na)
  const fb = Number.isFinite(nb)
  if (fa && fb) return na - nb
  if (fa) return -1
  if (fb) return 1
  return a < b ? -1 : a > b ? 1 : 0
}

/**
 * Achata o mapa de temporadas numa lista ordenada e deduplicada de episódios.
 * Ordem: temporada ↑ (numérica; não-numéricas por último), episódio ↑, estável.
 * Dedup por id (dados Xtream repetem o mesmo id entre temporadas) mantendo a
 * ocorrência da MENOR temporada.
 */
export function flattenEpisodes<T extends EpisodeLike>(seasons: SeasonsMap<T>): FlatEpisode<T>[] {
  if (!seasons || typeof seasons !== 'object') return []
  const entries: FlatEpisode<T>[] = []
  const seen = new Set<string>()
  // 1ª passada em ordem de temporada crescente → o dedup mantém a menor temporada.
  const keys = Object.keys(seasons).sort(compareSeasonKeys)
  let order = 0
  for (const key of keys) {
    const eps = seasons[key]
    if (!Array.isArray(eps)) continue
    for (let i = 0; i < eps.length; i++) {
      const ep = eps[i]
      if (!ep || typeof ep.id !== 'string' || ep.id.length === 0 || seen.has(ep.id)) continue
      seen.add(ep.id)
      const seasonNumber = seasonNumberFor(key, ep)
      const rawEpNum = ep.episode_num
      const parsed = rawEpNum != null ? Number(rawEpNum) : Number.NaN
      // episode_num ausente/inválido → posição na temporada (1-based) como fallback.
      const episodeNumber = Number.isFinite(parsed) ? parsed : i + 1
      entries.push({
        id: ep.id,
        seasonNumber,
        episodeNumber,
        seasonKey: key,
        order: order++,
        episode: ep,
      })
    }
  }
  entries.sort((a, b) => {
    const sa = seasonSortValue(a.seasonNumber)
    const sb = seasonSortValue(b.seasonNumber)
    if (sa !== sb) return sa - sb
    if (a.episodeNumber !== b.episodeNumber) return a.episodeNumber - b.episodeNumber
    return a.order - b.order
  })
  return entries
}

/**
 * Próximo episódio na sequência a partir de `currentEpisodeId`:
 * mesma temporada, episódio seguinte; ao acabar a temporada, 1º episódio da
 * PRÓXIMA temporada. No fim da série → null (nunca volta pro começo nem pula
 * pra temporada aleatória). id desconhecido → null (não advança).
 */
export function resolveNextEpisode<T extends EpisodeLike>(
  seasons: SeasonsMap<T>,
  currentEpisodeId: string | null | undefined,
): FlatEpisode<T> | null {
  if (!currentEpisodeId) return null
  const flat = flattenEpisodes(seasons)
  const idx = flat.findIndex((e) => e.id === currentEpisodeId)
  if (idx < 0) return null
  return idx < flat.length - 1 ? flat[idx + 1]! : null
}

// ===========================================================================
// ONDE O USUÁRIO PAROU — a DECISÃO, não só a ORDEM.
// ===========================================================================
//
// ── O bug que isto conserta (produção, conta do Fernando, série Silo) ──
// "Na série marca corretamente, na home e outras páginas não."
//
// O cabeçalho deste arquivo já prometia "uma ÚNICA fonte de verdade, usada pela
// ficha, pelo player e pelo snapshot de progresso". Só a ORDEM
// (`flattenEpisodes`) era compartilhada. A DECISÃO de qual é o próximo estava
// escrita DUAS vezes, com regras DIFERENTES:
//
//   ficha (SeriesDetailsScreen.nextToWatch): âncora finished → `flat[idx + 1]`,
//     o SEGUINTE NA ORDEM, tenha ele sido assistido antes ou não;
//   snapshot (local-db.computeNextEpisode): âncora finished → o primeiro à
//     frente AINDA NÃO FINISHED, pulando tudo que já está marcado.
//
// As duas concordam enquanto o episódio seguinte estiver por assistir. Elas
// EXPLODEM quando o usuário RE-ASSISTE uma série que já marcou como vista — e é
// exatamente o estado da conta do dono: a linha real de `swipe_sync_items`
// guardava `nextSeason: 3, nextEpisode: 7` (o único episódio ainda não-finished
// de Silo, lá na 3ª temporada) enquanto ele estava re-assistindo a temporada 2.
// A ficha dizia T2:E2; a home, alimentada pelo snapshot, apontava T3:E7.
//
// O `anchorIdx === -1` do snapshot tinha a MESMA divergência por outro caminho:
// com a âncora fora do catálogo ele varria do começo e voltava pro S1E1,
// enquanto a ficha simplesmente tentava a próxima position mais recente.
//
// ── A regra, agora única ──
// É a da ficha, que é a que o dono do produto declarou correta. Ela responde
// "continuar de onde parei", não "achar o que falta assistir": o que manda é a
// RECÊNCIA (a intenção mais recente do usuário), e o passo é sempre o PRÓXIMO
// NA ORDEM. Marcar temporadas como vistas não pode teletransportar ninguém.

/** Subconjunto de PlaybackPosition que a decisão consome. Estrutural de
 *  propósito: mantém a função pura e chamável dos dois lados sem acoplar ao
 *  schema do IndexedDB. */
export interface ContinuePositionLike {
  episodeId?: string | undefined
  /** Ausente conta como NÃO terminado (o schema do IDB deixa opcional). */
  finished?: boolean | undefined
  /** Época de qualquer unidade — só é comparado consigo mesmo. */
  lastWatchedAt: number
  positionSec?: number | undefined
}

/** Onde o usuário deve retomar. `index` aponta em `orderedEpisodeIds`. */
export type ContinuePoint =
  /** Mais recente válido EM ANDAMENTO → retomar ele, no segundo em que parou. */
  | { kind: 'resume'; index: number; positionSec: number }
  /** Mais recente válido TERMINADO → o seguinte na ordem, do começo. */
  | { kind: 'next'; index: number; positionSec: 0 }
  /** Mais recente válido terminado E era o ÚLTIMO → não há próximo. */
  | { kind: 'finished' }
  /** Nenhuma position casa com o catálogo → nada tocado (ou tudo removido). */
  | { kind: 'untouched' }

/**
 * Decide onde retomar, ancorando na position MAIS RECENTE que ainda existe no
 * catálogo.
 *
 * Por que iterar a lista de recência em vez de pegar só o máximo: provider
 * Xtream rotaciona/remove episódios, e a âncora pode ter sumido do catálogo.
 * Nesse caso a resposta certa é a PRÓXIMA intenção mais recente do usuário —
 * nunca "o primeiro buraco do início", que joga quem pula episódios de volta
 * pro S1E1.
 */
export function resolveContinuePoint(
  orderedEpisodeIds: ReadonlyArray<string>,
  positions: ReadonlyArray<ContinuePositionLike>,
): ContinuePoint {
  if (orderedEpisodeIds.length === 0) return { kind: 'untouched' }

  const byRecency = positions
    .filter((p) => typeof p.episodeId === 'string' && p.episodeId.length > 0)
    .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)

  for (const pos of byRecency) {
    const idx = orderedEpisodeIds.indexOf(pos.episodeId as string)
    if (idx < 0) continue // episódio saiu do catálogo — tenta a próxima intenção

    if (!pos.finished) {
      return { kind: 'resume', index: idx, positionSec: pos.positionSec ?? 0 }
    }
    // Terminado → o SEGUINTE NA ORDEM. Não se pula o que já está finished: quem
    // re-assiste continua na sequência, e foi o pulo que mandava a home pra
    // outra temporada.
    if (idx < orderedEpisodeIds.length - 1) {
      return { kind: 'next', index: idx + 1, positionSec: 0 }
    }
    return { kind: 'finished' }
  }

  return { kind: 'untouched' }
}
